import { TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

interface DateRangeInputProps {
  label: string;
  startValue: string;
  endValue: string;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  maxMonths?: number;
}

/**
 * Componente para un par de fechas (inicio/fin)
 * Muestra validación visual con borde rojo
 */
const DateRangeInput: React.FC<DateRangeInputProps> = ({
  label,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  maxMonths,
}) => {
  const [startError, setStartError] = useState(false);
  const [endError, setEndError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const isCompleteDateString = (value: string): boolean => {
    if (!value || typeof value !== 'string') return false;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(value)) return false;
    const date = new Date(value);
    return !isNaN(date.getTime());
  };

  const extractDateOnlyFromString = (value: string): string => {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const dateMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return dateMatch ? dateMatch[1] : '';
  };

  const getInitialStartValue = () =>
    extractDateOnlyFromString(startValue || '');
  const getInitialEndValue = () => extractDateOnlyFromString(endValue || '');

  // Estados locales para manejar valores mientras el usuario escribe
  const [localStartValue, setLocalStartValue] =
    useState<string>(getInitialStartValue);
  const [localEndValue, setLocalEndValue] =
    useState<string>(getInitialEndValue);

  const isWritingStartRef = useRef<boolean>(false);
  const isWritingEndRef = useRef<boolean>(false);

  const localStartValueRef = useRef<string>(localStartValue);
  const localEndValueRef = useRef<string>(localEndValue);

  useEffect(() => {
    localStartValueRef.current = localStartValue;
  }, [localStartValue]);

  useEffect(() => {
    localEndValueRef.current = localEndValue;
  }, [localEndValue]);

  useEffect(() => {
    if (isWritingStartRef.current) {
      return;
    }

    const normalizedStart = extractDateOnlyFromString(startValue || '');
    const currentLocal = localStartValueRef.current;

    if (normalizedStart !== currentLocal) {
      if (isCompleteDateString(normalizedStart) || normalizedStart === '') {
        setLocalStartValue(normalizedStart);
      }
    }
  }, [startValue]);

  useEffect(() => {
    if (isWritingEndRef.current) {
      return;
    }

    const normalizedEnd = extractDateOnlyFromString(endValue || '');
    const currentLocal = localEndValueRef.current;

    if (normalizedEnd !== currentLocal) {
      if (isCompleteDateString(normalizedEnd) || normalizedEnd === '') {
        setLocalEndValue(normalizedEnd);
      }
    }
  }, [endValue]);

  useEffect(() => {
    validateRange(startValue, endValue);
  }, [startValue, endValue, maxMonths]);

  const validateRange = (start: string, end: string) => {
    if (start && !end) {
      setStartError(false);
      setEndError(true);
      setErrorMessage('Debes agregar una fecha de fin');
      return false;
    }

    if (start && end && new Date(start) > new Date(end)) {
      setStartError(true);
      setEndError(true);
      setErrorMessage('La fecha de inicio no puede ser mayor a la de fin');
      return false;
    }

    if (maxMonths !== undefined && start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const yearDiff = endDate.getFullYear() - startDate.getFullYear();
      const monthDiff = endDate.getMonth() - startDate.getMonth();
      const diffInMonths = yearDiff * 12 + monthDiff;

      if (diffInMonths > maxMonths) {
        setStartError(true);
        setEndError(true);
        setErrorMessage(
          `La diferencia no puede ser mayor a ${maxMonths} meses`,
        );
        return false;
      }
    }

    setStartError(false);
    setEndError(false);
    setErrorMessage('');
    return true;
  };

  const handleStartChange = (value: string) => {
    isWritingStartRef.current = true;
    setLocalStartValue(value);
    localStartValueRef.current = value;
    if (isCompleteDateString(value) || !value) {
      onStartChange(value);
    }

    setTimeout(() => {
      isWritingStartRef.current = false;
    }, 100);
  };

  const handleEndChange = (value: string) => {
    isWritingEndRef.current = true;
    setLocalEndValue(value);
    localEndValueRef.current = value;
    if (isCompleteDateString(value) || !value) {
      onEndChange(value);
    }

    setTimeout(() => {
      isWritingEndRef.current = false;
    }, 100);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Typography variant="body2" marginBottom={1}>
        {label}
      </Typography>
      <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <TextField
            label="Fecha de inicio"
            type="date"
            size="small"
            value={localStartValue || ''}
            onChange={(e) => handleStartChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={startError}
            fullWidth
          />
          <TextField
            label="Fecha de fin"
            type="date"
            size="small"
            value={localEndValue || ''}
            onChange={(e) => handleEndChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            error={endError}
            fullWidth
          />
        </div>
        {errorMessage && (
          <Typography variant="caption" color="error" sx={{ marginTop: -0.5 }}>
            {errorMessage}
          </Typography>
        )}
      </div>
    </div>
  );
};

export default DateRangeInput;
